<template>
  <div
    :class="[
      'page-list',
      'page-list--transat-service'
    ]"
  >

    <ui-wad class="d-flex align-items justify-content-space-between mt-1 mb-3">

      <ui-wad
        v-hide="providerCallCount === 0"
        class="font-weight-light"
      >
        <layout-misc-float :value="context.total" />
        {{
          context.total <= 1 ?
            `résultat` :
            `résultats`
        }}
      </ui-wad>

      <ui-tooltip-wrapper
        :status="hasACL(ACLS.LEAVE_TEAM_CREATE) ?
          UI.STATUS.DISABLED :
          UI.STATUS.ENABLED
        "
        v-bind="{
          title: `Vous n'avez pas les droits pour effectuer cette action`,
          variant: UI.VARIANT.LIGHT,
        }"
      >
        <layout-cta-sub
          component="ui-link-button"
          :status="hasACL(ACLS.LEAVE_TEAM_CREATE) ?
            UI.STATUS.ENABLED :
            UI.STATUS.DISABLED
          "
          :variant="UI.VARIANT.PRIMARY"
          :size="UI.SIZE.SMALL"
          :route="getCreationRoute()"
        >
          <ui-icon
            name="group_add"
          />
          &nbsp;
          Ajouter une équipe
        </layout-cta-sub>
      </ui-tooltip-wrapper>

    </ui-wad>

    <ui-table-wrapper
      ref="table"
      :provider="provider"
      :initial-sort-by="context.order"
      :initial-sort-desc="context.sort === 'desc'"
      :fields="fields"
      :size="UI.SIZE.SMALL"
      hover
      striped
      fixed
      @row-clicked="({ item }) => openDetailsIntent(item)"
    >

      <template v-if="providerCallCount === 0" #table-busy>
        <layout-placeholder-table-body
          :count="context.perPage"
          striped
        />
      </template>

      <template #cell(actions)="data">
        <div class="page-list__actions__inner">

          <ui-link-icon
            :class="[
              'page-list__open-cta',
              'font-size-20px'
            ]"
            name="arrow_outward"
            :route="getDetailsRoute(data.item.uuidTeam)"
            :elevation="UI.ELEVATION.LEVEL_0"
            no-animation
          />

        </div>
      </template>

      <template #cell(collaboratorCount)="data">
        <span class="font-size-100 line-height-0 font-family-monospace w-3 text-align-right d-inline-block font-weight-bold">{{
          data.item.collaboratorCount
        }}</span>
        <ui-blank class="mr-1" />
        <span class="font-size-80 text-transform-uppercase color-gray-dark">
          {{
            data.item.collaboratorCount > 1 ?
              `salariés` :
              `salarié`
          }}
        </span>
      </template>

      <template #cell(approver)="data">
        <span class="font-size-95 color-gray-deep">{{ data.item.approver.nom }}</span>
        <ui-blank class="mr-0.5" />
        <span class="color-gray-dark font-weight-light">{{ data.item.approver.mail }}</span>
      </template>

    </ui-table-wrapper>


    <div class="page-list__footer">

      <ui-blank />

      <ui-table-pagination
        v-model="context.currentPage"
        :total-rows="context.total"
        :per-page="context.perPage"
        align="center"
      />

      <ui-wad class="d-flex align-items-center gap-1 flex-shrink-0 flex-direction-row-reverse">
        <ui-wad class="font-size-90 font-weight-light">
          par page
        </ui-wad>
        <ui-form-select
          v-model="context.perPage"
          :options="perPageOptions"
          :size="UI.SIZE.SMALL"
          class="page-list__filters__per-page"
        />
      </ui-wad>

    </div>

  </div>

  <nuxt-page />

</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members
@import ~/assets/sass/modules/form/variables

$actions-column-width: 6vw

.page-list
  $self: &

  padding-top: $layout-spacer-y * .5
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  &__footer
    margin-top: $layout-spacer-y
    display: flex
    align-items: center
    justify-content: space-between

  /////////////////////////////////

  :deep(.b-table.table)
    @include table-presets
    @include table-row-hovering( #{ $self }__open-cta )

    /// Columns
    #{ $self }__actions
      width: $actions-column-width

    /// Cells
    tbody

      #{ $self }__actions
        @include table-cell-presets
          padding-left: space-x(1.5)

</style>

<script lang="ts">
import Console from '~/utils/lib/core/console'
import { debounce, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getCreationRoute, getDetailsRoute, type Service } from '~/stores/transat/service'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useLinkHandler } from '~/composables/core/link-handler'
import type { UiTableWrapper } from '#components'
import { useTableProvider, type Context } from '~/composables/layout/table-provider'
import { wrap } from '~/utils/infra/common/types'
import { EVENTS, useStore_transat_service_list } from '~/stores/transat/service/list'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { useStore_transat_layout } from '~/stores/transat/layout'

const SEARCH_EXECUTION_DEBOUNCE_DELAY = 300

const console = new Console('component:transat-service-overview')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.ABSENCES,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Équipes — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const ToastBuilder = useToastBuilder()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const providerDefaultData = {
      context: {
        order: 'name',
        sort: 'asc',
      } as Partial<Context>
    }

    const tableProvider = useTableProvider<Service>(ref_table, providerDefaultData)

    const store_transat_layout = useStore_transat_layout()
    const store_service_list = useStore_transat_service_list()

    store_transat_layout.setNavSection('service')

    provide(
      EVENTS.ON_SERVICE_LIST_REFRESH_NEEDED,
      () => tableProvider.refreshTable()
    )

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...useLinkHandler(),
      ...tableProvider,
      ToastBuilder,
      store_service_list,
    }
  },
  data() {
    return {
      fields: [
        {
          key: 'actions',
          label: '',
          class: 'page-list__actions'
        },
        {
          key: 'name',
          label: `Nom de l'équipe`,
          sortable: true,
        },
        {
          key: 'approver',
          label: `Approbateur`
        },
        {
          key: 'collaboratorCount',
          label: 'Membres',
        },
      ],
      perPageOptions: [
        {
          value: 10,
          text: '10'
        },
        {
          value: 30,
          text: '30',
        },
        {
          value: 50,
          text: '50'
        }
      ],
      debouncedSearchChange: null as unknown as Function, // (lazy),
    }
  },
  created() {
    this.consumer = (context) => {
      const options = {
        input: wrap(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_service_list.read(options)
        .catch(tapCatch(err => {
          console.warn('consumer', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste des équipes`)
            .show()
        }))
    }

    this.debouncedSearchChange = debounce(
      SEARCH_EXECUTION_DEBOUNCE_DELAY,
      this.setSearch
    )
  },
  methods: {
    getCreationRoute() {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getCreationRoute(id, code_societe)
    },
    getDetailsRoute(uuidTeam) {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getDetailsRoute(id, code_societe, uuidTeam)
    },
    openDetailsIntent({ uuidTeam }) {
      const url = this.getDetailsRoute(uuidTeam)

      this.goToRoute(url)
    },
  }
})
</script>
